import orderBy from 'lodash/orderBy';
import { Action } from 'typescript-fsa';

import { createReducer } from '@topconagriculture/frontend-utils';

import { SharingMessage, SharingPartner, SharingStatus, SharingType } from '../../../core/api/sharing/sharing.types';
import { deleteSharePartner, editInvite, getShares, leaveInvitation, sendShareInvite } from './sharing.actions';

export type SharingState = {
  readonly shares: SharingMessage[];
  readonly partners: SharingPartner[];
};

export const initialState: SharingState = {
  shares: [],
  partners: [],
};

const sharingReducer = createReducer(initialState, {
  [getShares.done.type]: (state: SharingState, action: Action<{ params: string; result: SharingMessage[] }>) => {
    const { params: companyId } = action.payload;
    const { result: shares } = action.payload;

    const partners: SharingPartner[] = shares.map((share) => {
      const isReceived: boolean = companyId === share.sharedToCompanyId;
      const isSent: boolean = companyId === share.sharedByCompanyId;
      return {
        id: share.id,
        name: (isReceived ? share.sharedByCompanyName : share.sharedToCompanyName) || null,
        mail: isReceived ? share.sender : share.inviteEmail,
        status: SharingStatus[share.status.toUpperCase()],
        sharedItems: share.sharedItems,
        type: isSent ? SharingType.SENT : isReceived ? SharingType.RECEIVED : null,
        date: share.inviteDate,
        inviteToken: share.inviteToken,
        metadata: share.metadata,
      };
    });
    return { ...state, partners: orderBy(partners, 'date', 'desc'), shares };
  },
  [deleteSharePartner.done.type]: (state: SharingState, action: ReturnType<typeof deleteSharePartner.done>) => {
    const id = action.payload.params.id;
    return {
      shares: state.shares.filter((currentShare: SharingMessage) => currentShare.id !== id),
      partners: state.partners.filter((currentPartner: SharingPartner) => currentPartner.id !== id),
    };
  },
  [leaveInvitation.done.type]: (state: SharingState, action: ReturnType<typeof leaveInvitation.done>) => {
    const id = action.payload.params.id;
    return {
      shares: state.shares.filter((currentShare: SharingMessage) => currentShare.id !== id),
      partners: state.partners.filter((currentPartner: SharingPartner) => currentPartner.id !== id),
    };
  },
  [sendShareInvite.done.type]: (
    state: SharingState,
    action: Action<{ params: string; result: { id: string; inviteToken: string } }>,
  ) => {
    const { params: currentInvitationId } = action.payload;
    const { id: newPartnerId } = action.payload.result;
    if (!!currentInvitationId) {
      return {
        ...state,
        partners: [
          ...state.partners.map((partner) =>
            partner.id === currentInvitationId ? { ...partner, id: newPartnerId } : partner,
          ),
        ],
        shares: [
          ...state.shares.map((share) => (share.id === currentInvitationId ? { ...share, id: newPartnerId } : share)),
        ],
      };
    }
    return state;
  },
  [editInvite.done.type]: (state: SharingState, action: Action<{ params: string; result: SharingMessage }>) => {
    const { result, params: id } = action.payload;
    return {
      ...state,
      partners: [
        ...state.partners.map((partner) => (partner.id === id ? { ...partner, metadata: result.metadata } : partner)),
      ],
      shares: [...state.shares.map((share) => (share.id === id ? { ...share, metadata: result.metadata } : share))],
    };
  },
});

export default sharingReducer;
